import React from "react";
import { Box, Checkbox, Chip, CircularProgress, makeStyles, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles((theme) => ({
  selectedOption: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  defaultOption: {
    color: "inherit",
    fontWeight: 400,
  },
  tagText: {
    cursor: "pointer",
    marginLeft: 6,
    maxWidth: 160,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      maxWidth: 110,
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: 80,
    },
  },
  tagsContainer: {
    display: "inline-flex",
    alignItems: "center",
    flex: "0 1 auto",
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  autocomplete: {
    "& .MuiAutocomplete-inputRoot": {
      maxHeight: 80,
      overflowY: "hidden",
      flexWrap: "nowrap",
    },
    "& .MuiInputBase-root": {
      overflowX: "hidden",
      flexWrap: "nowrap",
    },
  },
  optionLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
}));

const PAGE_SIZE = 20;

const MultiSelectAutocomplete = ({ options = [], selected = [], onChange, label, loading, error, maxVisibleTags = 2 }) => {
  const classes = useStyles();
  const [page, setPage] = React.useState(1);
  const [inputValue, setInputValue] = React.useState("");
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setPage(1);
  }, [options.length, inputValue]);

  const selectedIds = React.useMemo(() => new Set(selected.map((u) => u.id)), [selected]);

  const sortedOptions = React.useMemo(() => {
    const selectedSorted = [...selected].sort((a, b) => a.name.localeCompare(b.name));

    const othersSorted = options.filter((u) => !selectedIds.has(u.id)).sort((a, b) => a.name.localeCompare(b.name));

    return [...selectedSorted, ...othersSorted];
  }, [options, selected, selectedIds]);

  const filteredOptions = React.useMemo(() => {
    if (!inputValue.trim()) return sortedOptions;
    const lower = inputValue.toLowerCase();
    return sortedOptions.filter((o) => o.name.toLowerCase().includes(lower));
  }, [sortedOptions, inputValue]);

  const paginatedOptions = React.useMemo(() => {
    return filteredOptions.slice(0, page * PAGE_SIZE);
  }, [filteredOptions, page]);

  const handleScroll = React.useCallback((event) => {
    const listboxNode = event.currentTarget;

    const isBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10;

    if (isBottom) {
      setPage((prev) => prev + 1);
    }
  }, []);

  const handleTagsMouseDown = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((currentOpen) => !currentOpen);
  }, []);

  const renderOption = React.useCallback(
    (option) => {
      const isSelected = selectedIds.has(option.id);
      return (
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          key={option.id}
        >
          <Checkbox
            checked={isSelected}
            color="primary"
            size="small"
            style={{ padding: 4, marginRight: 8 }}
          />
          <Box className={`${classes.optionLabel} ${isSelected ? classes.selectedOption : classes.defaultOption}`}>{option.name}</Box>
          <Box
            ml={1}
            flexShrink={0}
            className={isSelected ? classes.selectedOption : classes.defaultOption}
          >
            ({option.id})
          </Box>
        </Box>
      );
    },
    [selectedIds, classes],
  );

  const renderTags = React.useCallback(
    (value) => {
      if (loading) return null;

      const allSelected = value.length === options.length && options.length > 0;

      if (allSelected) {
        return (
          <Box
            component="div"
            className={classes.tagsContainer}
            onMouseDown={handleTagsMouseDown}
          >
            <Typography
              component="span"
              className={classes.tagText}
              style={{ maxWidth: "none" }}
            >
              <FormattedMessage id="app.all" /> ({options.length})
            </Typography>
          </Box>
        );
      }

      const visibleTags = value.slice(0, maxVisibleTags);
      const hiddenCount = value.length - maxVisibleTags;

      return (
        <Box
          component="div"
          className={classes.tagsContainer}
          onMouseDown={handleTagsMouseDown}
        >
          {visibleTags.map((option, index) => (
            <React.Fragment key={option.id}>
              <Typography
                component="span"
                className={classes.tagText}
              >
                {option.name}
              </Typography>

              <span> ({option.id})</span>

              {index < visibleTags.length - 1 ? "," : ""}
            </React.Fragment>
          ))}

          {hiddenCount > 0 && (
            <Chip
              size="small"
              label={`+${hiddenCount}`}
              style={{ marginLeft: 4 }}
            />
          )}
        </Box>
      );
    },
    [handleTagsMouseDown, loading, options.length, classes],
  );

  return (
    <Autocomplete
      multiple
      options={paginatedOptions}
      value={selected}
      disableCloseOnSelect
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      openOnFocus
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={(option) => `${option.name} (${option.id})`}
      loading={loading}
      size="small"
      className={classes.autocomplete}
      ListboxProps={{
        onScroll: handleScroll,
        style: {
          maxHeight: 300,
          overflow: "auto",
        },
      }}
      noOptionsText={
        error ? (
          <FormattedMessage
            id={error}
            defaultMessage={error}
          />
        ) : loading ? (
          <FormattedMessage id="app.loading.fetching.user" />
        ) : (
          <FormattedMessage id="app.user.notFound" />
        )
      }
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={renderOption}
    />
  );
};

export default MultiSelectAutocomplete;
